import { useTranslation } from 'react-i18next';
import { cn } from '@shared/lib';
import type { AdminElevenlabs, AdminStories, AdminStoriesDemo } from '../api/adminStoriesApi';
import { fmtCalendarDay, fmtDate, fmtInt, fmtPct } from '../model/format';
import { BarList, Panel, Tile } from './AdminAnalytics';

/**
 * «Истории» в аналитике: открывают ли игру, доходят ли до концовки, перепроходят ли, где бросают, ждут ли главу 2,
 * играют ли в задание дня и босса. Сверху — остаток кредитов ElevenLabs (просьба владельца 2026-10-22). Внизу — демо
 * без регистрации по дням (владелец 2026-10-25: «сколько демо в день»).
 */
export function AdminStoriesView({ data, eleven, lang }: { data: AdminStories; eleven?: AdminElevenlabs; lang: string }) {
  const { t } = useTranslation();
  const low = eleven?.configured && !eleven.error && eleven.limit > 0 && eleven.left < 20000;
  const weekChars = (eleven?.days ?? []).reduce((sum, d) => sum + d.chars, 0);
  return (
    <section>
      <h2 className="mb-1 font-serif text-[22px] font-medium tracking-[-0.01em]">{t('adminStories.title')}</h2>
      <div className="mb-3 max-w-[680px] text-[14px] leading-relaxed text-muted">{t('adminStories.sub')}</div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Tile
          className={cn(low && 'shadow-[inset_0_0_0_1px_#e6453b]')}
          label={t('adminStories.eleven.left')}
          value={!eleven ? '…' : !eleven.configured ? '—' : eleven.error ? '?' : fmtInt(eleven.left)}
          sub={
            !eleven
              ? undefined
              : !eleven.configured
                ? t('adminStories.eleven.notConfigured')
                : eleven.error
                  ? t('adminStories.eleven.error', { error: eleven.error })
                  : [
                      t('adminStories.eleven.of', { limit: fmtInt(eleven.limit) }),
                      eleven.resetsAt ? t('adminStories.eleven.resets', { date: fmtDate(eleven.resetsAt, lang) }) : null,
                      eleven.tier ? t('adminStories.eleven.tier', { tier: eleven.tier }) : null,
                      low ? t('adminStories.eleven.low') : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')
          }
        />
        <Tile label={t('adminStories.opened')} value={fmtInt(data.openedUsers)} sub={t('adminStories.openedSub')} />
        <Tile
          label={t('adminStories.started')}
          value={fmtInt(data.startedUsers)}
          sub={t('adminStories.startedSub', { runs: fmtInt(data.runs), finished: fmtInt(data.finishedRuns) })}
        />
        <Tile
          label={t('adminStories.replay')}
          value={fmtInt(data.replayUsers)}
          sub={t('adminStories.replaySub', { share: data.startedUsers ? fmtPct(data.replayUsers / data.startedUsers) : '—' })}
        />
      </div>

      <div className="mt-3 grid gap-3 lg:grid-cols-3">
        <Panel title={t('adminStories.endings')}>
          {data.endings.length ? (
            <BarList
              rows={data.endings.map((e) => ({ label: t(`adminStories.endingNames.${e.id}`, { defaultValue: e.id }), value: e.count }))}
              total={data.finishedRuns}
            />
          ) : (
            <p className="text-[14px] text-muted">{t('adminStories.empty')}</p>
          )}
        </Panel>
        <Panel title={t('adminStories.dropped')} hint={t('adminStories.droppedHint')}>
          {data.droppedAt.length ? (
            <BarList rows={data.droppedAt.map((d) => ({ label: t('adminStories.turn', { n: d.turn }), value: d.count }))} total={data.droppedAt.reduce((s, d) => s + d.count, 0)} />
          ) : (
            <p className="text-[14px] text-muted">{t('adminStories.empty')}</p>
          )}
        </Panel>
        <Panel title={t('adminStories.more')}>
          <dl className="space-y-2 text-[14px]">
            {[
              [t('adminStories.avgTurns'), data.avgTurns === null ? '—' : String(data.avgTurns).replace('.', ',')],
              [t('adminStories.voiceShare'), data.turns ? fmtPct(data.voiceTurns / data.turns) : '—'],
              [t('adminStories.fallbacks'), fmtInt(data.fallbackTurns)],
              [t('adminStories.interest'), t('adminStories.interestValue', { yes: fmtInt(data.interest.yes), like: fmtInt(data.interest.like) })],
              [t('adminStories.daily'), t('adminStories.dailyValue', { users: fmtInt(data.daily.users), runs: fmtInt(data.daily.runs), done: fmtInt(data.daily.done) })],
              [
                t('adminStories.boss'),
                t('adminStories.bossValue', {
                  users: fmtInt(data.boss.users),
                  fights: fmtInt(data.boss.fights),
                  wins: fmtInt(data.boss.wins),
                  resolved: fmtInt(data.boss.resolvedMistakes),
                }),
              ],
              [t('adminStories.tokens'), `${fmtInt(data.llmTokens.input)} / ${fmtInt(data.llmTokens.output)}`],
              [t('adminStories.tts'), t('adminStories.ttsValue', { period: fmtInt(data.ttsChars), week: fmtInt(weekChars) })],
            ].map(([label, value]) => (
              <div key={label} className="flex items-baseline justify-between gap-3">
                <dt className="text-muted">{label}</dt>
                <dd className="text-right font-medium tabular-nums text-fg">{value}</dd>
              </div>
            ))}
          </dl>
        </Panel>
      </div>

      <DemoBlock demo={data.demo} lang={lang} />
    </section>
  );
}

function DemoBlock({ demo, lang }: { demo: AdminStoriesDemo; lang: string }) {
  const { t } = useTranslation();
  const share = (n: number) => (demo.started ? fmtPct(n / demo.started) : '—');
  return (
    <div className="mt-3 grid gap-3 lg:grid-cols-3">
      <Panel title={t('adminStories.demo.title')} hint={t('adminStories.demo.hint', { perIp: demo.perIp, turns: demo.maxTurns })}>
        <dl className="space-y-2 text-[14px]">
          {[
            [t('adminStories.demo.today'), t('adminStories.demo.todayValue', { n: fmtInt(demo.today), limit: fmtInt(demo.limit) })],
            [t('adminStories.demo.started'), fmtInt(demo.started)],
            [t('adminStories.demo.finished'), `${fmtInt(demo.finished)} · ${share(demo.finished)}`],
            [t('adminStories.demo.done'), `${fmtInt(demo.done)} · ${share(demo.done)}`],
            [t('adminStories.demo.voice'), `${fmtInt(demo.voice)} · ${share(demo.voice)}`],
            [t('adminStories.tokens'), `${fmtInt(demo.llmTokens.input)} / ${fmtInt(demo.llmTokens.output)}`],
          ].map(([label, value]) => (
            <div key={label} className="flex items-baseline justify-between gap-3">
              <dt className="text-muted">{label}</dt>
              <dd className="text-right font-medium tabular-nums text-fg">{value}</dd>
            </div>
          ))}
        </dl>
      </Panel>
      <Panel title={t('adminStories.demo.byDay')} className="lg:col-span-2">
        {demo.days.length ? (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[420px] text-[14px] tabular-nums">
              <thead>
                <tr className="text-left text-[12px] text-muted">
                  <th className="pb-2 font-medium">{t('adminStories.demo.day')}</th>
                  <th className="pb-2 text-right font-medium">{t('adminStories.demo.started')}</th>
                  <th className="pb-2 text-right font-medium">{t('adminStories.demo.finished')}</th>
                  <th className="pb-2 text-right font-medium">{t('adminStories.demo.done')}</th>
                  <th className="pb-2 text-right font-medium">{t('adminStories.demo.voice')}</th>
                  <th className="pb-2 text-right font-medium">{t('adminStories.demo.avgTurns')}</th>
                </tr>
              </thead>
              <tbody>
                {demo.days.map((d) => (
                  <tr key={d.day} className="border-t border-solid border-[#2a2a30]">
                    <td className="py-1.5 text-fg">{fmtCalendarDay(d.day, lang)}</td>
                    <td className="py-1.5 text-right font-semibold text-fg">{fmtInt(d.started)}</td>
                    <td className="py-1.5 text-right text-fg">{fmtInt(d.finished)}</td>
                    <td className="py-1.5 text-right text-fg">{fmtInt(d.done)}</td>
                    <td className="py-1.5 text-right text-fg">{fmtInt(d.voice)}</td>
                    <td className="py-1.5 text-right text-muted">{d.avgTurns === null ? '—' : String(d.avgTurns).replace('.', ',')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-[14px] text-muted">{t('adminStories.demo.empty')}</p>
        )}
      </Panel>
    </div>
  );
}
